'use client';

import 'swiper/css';
import 'swiper/css/free-mode';

import { FreeMode } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import CardDecoListItem, { CardDecoListItemSkeleton } from '@/app/(football)/[team]/_components/common/CardDecoListItem';
import { useTeamConfig } from '@/app/(football)/[team]/_context/context';
import SectionHeader from '@/app/(football)/[team]/main/_components/Section/SectionHeader';

interface GalleryCardData {
  nickName: string;
  profileImage: string;
  metadataIdx: number;
  cardName: string;
  image: string;
  width: number;
  height: number;
  stickers: Array<{
    idx: string;
    src: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
  }>;
}

interface CardGallerySectionProps {
  galleryData?: GalleryCardData[];
  isLoading?: boolean;
  isError?: boolean;
}

export default function CardGallerySection({
  galleryData = [],
  isLoading = false,
  isError = false,
}: CardGallerySectionProps) {
  const { teamSlug } = useTeamConfig();
  const isDataLoading = !isError && isLoading;
  const isDataAvailable = !isError && !isLoading && galleryData.length > 0;

  return (
    <section className="flex w-full flex-col gap-5">
      <div className="px-5">
        <SectionHeader color="--color-g-00">세상에 하나뿐인 대표카드 갤러리</SectionHeader>
      </div>

      <div className="h-[229px] w-full overflow-hidden">
        {/* Error State */}
        {isError && (
          <div className="flex h-full flex-col items-center gap-3 pt-9">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="18" cy="18" r="16" fill="var(--color-team-brand-02)" fillOpacity="0.2" />
              <circle cx="18" cy="18" r="12" fill="var(--color-team-brand-02)" />
              <path d="M18 12V20" stroke="white" strokeWidth="2" strokeLinecap="round" />
              <circle cx="18" cy="24" r="1" fill="white" />
            </svg>
            <span className="typo-body-5 w-full text-center text-white">다시 접속해 주세요</span>
          </div>
        )}

        {/* Loading State */}
        {isDataLoading && (
          <div className="flex gap-3 px-5">
            {Array.from({ length: 5 }, (_, index) => (
              <CardDecoListItemSkeleton key={index} teamSlug={teamSlug} />
            ))}
          </div>
        )}

        {/* Data Available */}
        {isDataAvailable && (
          <Swiper
            modules={[FreeMode]}
            freeMode={true}
            slidesPerView="auto"
            spaceBetween={12}
            slidesOffsetBefore={20}
            slidesOffsetAfter={20}
            observer={true}
            observeParents={true}
            resizeObserver={true}
            className="h-full"
          >
            {galleryData.map((item, index) => (
              <SwiperSlide key={index} style={{ width: 136 }}>
                <CardDecoListItem
                  nickName={item.nickName}
                  profileImage={item.profileImage}
                  image={item.image}
                  width={item.width}
                  height={item.height}
                  stickers={item.stickers}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
    </section>
  );
}
